import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Trash2, Edit2, Save, X, TrendingUp, 
  DollarSign, Calendar, Tag, RefreshCw, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_CATEGORIES = [
  'Services',
  'Product Sales', 
  'Consulting',
  'Investments',
  'Rental Income',
  'Commissions',
  'Royalties',
  'Other'
];

function IncomeTab() {
  const [entries, setEntries] = useState([]);
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showManageSources, setShowManageSources] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [period, setPeriod] = useState('month');
  
  // Form state
  const [entryForm, setEntryForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    source_id: '',
    description: '',
    reference_number: ''
  });
  
  const [sourceForm, setSourceForm] = useState({
    name: '',
    category: '',
    description: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, sourcesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/income/entries`, { withCredentials: true }),
        axios.get(`${API}/income/sources`, { withCredentials: true }),
        axios.get(`${API}/income/summary?period=${period}`, { withCredentials: true })
      ]);
      setEntries(entriesRes.data.entries || []);
      setSources(sourcesRes.data.sources || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch income data:', error);
      toast.error('Failed to load income data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddSource = async (e) => {
    e.preventDefault();
    if (!sourceForm.name.trim()) {
      toast.error('Source name is required');
      return;
    }
    
    try {
      await axios.post(`${API}/income/sources`, sourceForm, { withCredentials: true });
      toast.success('Income source created');
      setSourceForm({ name: '', category: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create source');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Delete this income source? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/income/sources/${sourceId}`, { withCredentials: true });
      toast.success('Income source deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete source');
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.amount || !entryForm.source_id) {
      toast.error('Amount and source are required');
      return;
    }
    
    try {
      await axios.post(`${API}/income/entries`, {
        ...entryForm,
        amount: parseFloat(entryForm.amount)
      }, { withCredentials: true });
      toast.success('Income entry added');
      setEntryForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        source_id: '',
        description: '',
        reference_number: ''
      });
      setShowAddEntry(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    try {
      await axios.put(`${API}/income/entries/${editingEntry.entry_id}`, {
        amount: parseFloat(entryForm.amount),
        date: entryForm.date,
        source_id: entryForm.source_id,
        description: entryForm.description,
        reference_number: entryForm.reference_number
      }, { withCredentials: true });
      toast.success('Income entry updated');
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this income entry?')) return;
    
    try {
      await axios.delete(`${API}/income/entries/${entryId}`, { withCredentials: true });
      toast.success('Income entry deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const startEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      amount: entry.amount.toString(),
      date: entry.date,
      source_id: entry.source_id,
      description: entry.description || '',
      reference_number: entry.reference_number || ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Income Tracking</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Track your business income from various sources</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowManageSources(!showManageSources)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
            data-testid="manage-sources-btn"
          >
            <Settings className="w-4 h-4" />
            Manage Sources
          </button>
          <button
            onClick={() => setShowAddEntry(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
            data-testid="add-income-btn"
          >
            <Plus className="w-4 h-4" />
            Add Income
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Total Income</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
              ${summary.total_income?.toLocaleString()}
            </p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
              {summary.entry_count} entries this {period}
            </p>
          </div>
          
          {summary.by_source?.slice(0, 3).map((source, i) => (
            <div key={i} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Tag className="w-4 h-4" />
                <span className="text-sm truncate">{source.source_name}</span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                ${source.total?.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">{source.count} entries</p>
            </div>
          ))}
        </div>
      )}

      {/* Period Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500 dark:text-slate-400">Period:</span>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
        >
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Manage Sources Panel */}
      <AnimatePresence>
        {showManageSources && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
          >
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Income Sources</h3>
            
            {/* Add Source Form */}
            <form onSubmit={handleAddSource} className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
              <input
                type="text"
                placeholder="Source name *"
                value={sourceForm.name}
                onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                className="flex-1 min-w-[150px] px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                data-testid="source-name-input"
              />
              <select
                value={sourceForm.category}
                onChange={(e) => setSourceForm({ ...sourceForm, category: e.target.value })}
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                <option value="">Category (optional)</option>
                {SOURCE_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                data-testid="add-source-btn"
              >
                Add Source
              </button>
            </form>
            
            {/* Source List */}
            <div className="space-y-2">
              {sources.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No income sources yet. Add one above.</p>
              ) : (
                sources.map((source) => (
                  <div key={source.source_id} className="flex items-center justify-between py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{source.name}</p>
                      <p className="text-xs text-slate-500">
                        {source.category || 'Uncategorized'} • {source.usage_count || 0} entries
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteSource(source.source_id)}
                      className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
                      disabled={source.usage_count > 0}
                      title={source.usage_count > 0 ? 'Cannot delete - source has entries' : 'Delete source'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Entry Modal */}
      <AnimatePresence>
        {(showAddEntry || editingEntry) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddEntry(false); setEditingEntry(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-900 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {editingEntry ? 'Edit Income Entry' : 'Add Income Entry'}
                </h3>
                <button
                  onClick={() => { setShowAddEntry(false); setEditingEntry(null); }}
                  className="p-2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={editingEntry ? handleUpdateEntry : handleAddEntry} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={entryForm.amount}
                      onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })}
                      className="w-full pl-8 pr-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="0.00"
                      data-testid="income-amount-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={entryForm.date}
                    onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    data-testid="income-date-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Source *</label>
                  <select
                    required
                    value={entryForm.source_id}
                    onChange={(e) => setEntryForm({ ...entryForm, source_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    data-testid="income-source-select"
                  >
                    <option value="">Select a source</option>
                    {sources.map(source => (
                      <option key={source.source_id} value={source.source_id}>{source.name}</option>
                    ))}
                  </select>
                  {sources.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No sources yet. Add one in &quot;Manage Sources&quot; first.</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Description</label>
                  <input
                    type="text"
                    value={entryForm.description}
                    onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="Optional description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Reference #</label>
                  <input
                    type="text"
                    value={entryForm.reference_number}
                    onChange={(e) => setEntryForm({ ...entryForm, reference_number: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="Invoice or reference number"
                  />
                </div>
                
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowAddEntry(false); setEditingEntry(null); }}
                    className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                    data-testid="save-income-btn"
                  >
                    <Save className="w-4 h-4" />
                    {editingEntry ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Income Entries List */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white">Recent Income</h3>
        </div>
        
        {entries.length === 0 ? (
          <div className="p-8 text-center">
            <DollarSign className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No income entries yet</p>
            <p className="text-sm text-slate-400 dark:text-slate-500">Click &quot;Add Income&quot; to record your first entry</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {entries.map((entry) => (
              <div key={entry.entry_id} className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{entry.source_name}</p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(entry.date).toLocaleDateString()}</span>
                      {entry.description && (
                        <>
                          <span>•</span>
                          <span className="truncate max-w-[150px]">{entry.description}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                    +${entry.amount?.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEditEntry(entry)}
                      className="p-2 text-slate-400 hover:text-indigo-500 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEntry(entry.entry_id)}
                      className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default IncomeTab;
